<div class="relative min-h-screen bg-slate-50 -m-4 sm:-m-8">

  @if (data()?.loading) {
    <div class="absolute inset-0 z-40 flex items-center justify-center bg-slate-50/70 backdrop-blur-[1px]"
         role="status" aria-live="polite" aria-busy="true">
      <div class="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white px-8 py-6 shadow-lg">
        <i class="pi pi-spin pi-spinner text-3xl text-indigo-600"></i>
        <div class="text-center">
          <p class="text-sm font-semibold text-slate-800">Cargando pipeline comercial…</p>
          <p class="mt-0.5 text-xs text-slate-500">Analizando embudo de ventas</p>
        </div>
      </div>
    </div>
  }

  <div class="bg-white border-b border-slate-200 px-6 py-5">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <div class="flex items-center gap-2">
          <span class="flex items-center justify-center w-9 h-9 rounded-xl bg-indigo-600">
            <i class="pi pi-filter text-white text-base"></i>
          </span>
          <div>
            <h1 class="text-2xl font-bold text-slate-900">Forecast de pipeline comercial</h1>
            <p class="text-xs text-slate-500">Embudo de ventas · probabilidad de cierre</p>
          </div>
        </div>
        <p class="mt-1 text-sm text-slate-500 ml-11 capitalize">{{ todayLabel() }}</p>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <span class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium">
          <i class="pi pi-calendar text-slate-400"></i>
          {{ periodLabel() }}
        </span>

        <div class="flex rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm"
             [class.pointer-events-none]="data()?.loading"
             [class.opacity-60]="data()?.loading">
          @for (opt of horizonOptions; track opt.value) {
            <button type="button" (click)="setHorizon(opt.value)"
              [disabled]="data()?.loading"
              class="px-3 py-2 text-xs font-semibold transition-colors disabled:cursor-wait"
              [class.bg-indigo-600]="period().horizon === opt.value && opt.value !== 'custom'"
              [class.text-white]="period().horizon === opt.value && opt.value !== 'custom'"
              [class.text-slate-600]="period().horizon !== opt.value"
              [class.hover:bg-slate-50]="period().horizon !== opt.value && !data()?.loading">
              {{ opt.label }}
            </button>
          }
        </div>

        <button type="button" (click)="load()" [disabled]="data()?.loading"
          class="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 text-xs font-medium disabled:opacity-50 shadow-sm">
          <i class="pi pi-refresh text-sm" [class.animate-spin]="data()?.loading"></i>
          Actualizar
        </button>
      </div>
    </div>

    @if (period().horizon === 'custom') {
      <div class="mt-4 flex flex-wrap items-center gap-3 ml-11">
        <input type="date" [value]="customDateFrom()"
          (input)="customDateFrom.set($any($event.target).value)"
          class="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500">
        <span class="text-slate-400 text-sm">—</span>
        <input type="date" [value]="customDateTo()"
          (input)="customDateTo.set($any($event.target).value)"
          class="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500">
        <button type="button" (click)="applyCustomPeriod()"
          [disabled]="!customDateFrom() || !customDateTo() || !!data()?.loading"
          class="px-4 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
          Aplicar
        </button>
      </div>
    }
  </div>

  <div class="bg-indigo-800 text-white px-6 py-3 text-sm">
    <div class="flex flex-wrap items-center gap-x-8 gap-y-2">
      <span class="flex items-start gap-2 max-w-3xl">
        <i class="pi pi-info-circle mt-0.5 opacity-80"></i>
        <span>
          Proyecta el cierre comercial del embudo
          (<strong>Prospecto → Propuesta → Negociación → Aceptada</strong>)
          aplicando una probabilidad a cada etapa.
        </span>
      </span>
      @if (data()?.kpis; as k) {
        <span class="flex items-center gap-2.5 ml-auto shrink-0">
          <span class="opacity-90 inline-flex items-center gap-1.5 text-xs">
            Pipeline ponderado
            <i class="pi pi-question-circle text-indigo-100/90 cursor-help text-xs"
              [pTooltip]="help.weighted"
              tooltipPosition="left"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </span>
          <strong class="text-xl sm:text-2xl font-bold tabular-nums text-white leading-none">
            {{ k.weightedAmount | currency:'MXN':'symbol-narrow':'1.0-0' }}
          </strong>
        </span>
      }
    </div>
  </div>

  @if (data()?.error) {
    <div class="mx-6 mt-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 text-sm flex items-center gap-2">
      <i class="pi pi-exclamation-triangle text-amber-500"></i>
      {{ data()?.error }}
    </div>
  }

  <div class="p-6 space-y-5">

    <!-- KPIs -->
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="flex items-center justify-between">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
            Pipeline bruto
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem]"
              [pTooltip]="help.pipeline" tooltipPosition="bottom"
              tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
          </p>
          <span class="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <i class="pi pi-briefcase text-sm"></i>
          </span>
        </div>
        <p class="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
          {{ (data()?.kpis?.pipelineAmount ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
        <p class="mt-1 text-xs text-slate-500">
          {{ data()?.kpis?.pipelineCount ?? 0 }} oportunidades abiertas
        </p>
      </div>

      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="flex items-center justify-between">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
            Pipeline ponderado
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem]"
              [pTooltip]="help.weighted" tooltipPosition="bottom"
              tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
          </p>
          <span class="w-8 h-8 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center">
            <i class="pi pi-percentage text-sm"></i>
          </span>
        </div>
        <p class="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
          {{ (data()?.kpis?.weightedAmount ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
        <p class="mt-1 text-xs text-slate-500">
          Prob. promedio {{ (data()?.kpis?.avgProbability ?? 0) | percent:'1.0-0' }}
        </p>
      </div>

      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="flex items-center justify-between">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
            Aceptadas
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem]"
              [pTooltip]="help.accepted" tooltipPosition="bottom"
              tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
          </p>
          <span class="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <i class="pi pi-check-circle text-sm"></i>
          </span>
        </div>
        <p class="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
          {{ (data()?.kpis?.acceptedAmount ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
        <p class="mt-1 text-xs text-slate-500">
          {{ data()?.kpis?.acceptedCount ?? 0 }} ventas · 90% probabilidad
        </p>
      </div>

      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="flex items-center justify-between">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
            Win rate
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem]"
              [pTooltip]="help.winRate" tooltipPosition="left"
              tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
          </p>
          <span class="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
            <i class="pi pi-chart-line text-sm"></i>
          </span>
        </div>
        <p class="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
          {{ (data()?.kpis?.winRate ?? 0) | number:'1.0-0' }}%
        </p>
        <p class="mt-1 text-xs text-slate-500">
          Perdidas: {{ (data()?.kpis?.lostAmount ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
          · Creadas en periodo: {{ data()?.kpis?.createdInPeriod ?? 0 }}
          <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.65rem] ml-0.5"
            [pTooltip]="help.created" tooltipPosition="left"
            tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
        </p>
      </div>
    </div>

    <!-- Charts -->
    <div class="grid grid-cols-1 xl:grid-cols-12 gap-4">
      <div class="xl:col-span-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="mb-3">
          <h2 class="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            Embudo por etapa
            <i class="pi pi-question-circle text-slate-400 cursor-help text-xs"
              [pTooltip]="help.funnel" tooltipPosition="right"
              tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
          </h2>
          <p class="text-xs text-slate-500">Monto bruto vs ponderado por probabilidad</p>
        </div>
        <div class="h-72">
          <p-chart type="bar" [data]="funnelChart()" [options]="funnelOptions" styleClass="h-full w-full" />
        </div>
      </div>

      <div class="xl:col-span-4 space-y-4">
        <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 overflow-hidden">
          <h2 class="text-sm font-semibold text-slate-900 mb-1 inline-flex items-center gap-1.5">
            Composición
            <i class="pi pi-question-circle text-slate-400 cursor-help text-xs"
              [pTooltip]="help.composition" tooltipPosition="left"
              tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
          </h2>
          <p class="text-xs text-slate-500 mb-3">Pipeline abierto por etapa</p>
          <div class="flex items-center gap-3">
            <div class="relative shrink-0 w-28 h-28 overflow-hidden">
              <p-chart type="doughnut" [data]="compositionChart()" [options]="doughnutOptions" class="block h-full w-full" />
            </div>
            <ul class="min-w-0 flex-1 space-y-2 max-h-28 overflow-y-auto">
              @for (seg of compositionLegend(); track seg.label) {
                <li class="flex items-center gap-2 text-xs">
                  <span class="w-2.5 h-2.5 rounded-full shrink-0" [style.background]="seg.color"></span>
                  <span class="text-slate-600 truncate">{{ seg.label }}</span>
                  <span class="font-semibold text-slate-900 ml-auto tabular-nums pl-2">
                    {{ seg.amount | currency:'MXN':'symbol-narrow':'1.0-0' }}
                  </span>
                </li>
              } @empty {
                <li class="text-xs text-slate-400">Sin datos</li>
              }
            </ul>
          </div>
        </div>

        <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100">
            <h2 class="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
              Por tipo de venta
              <i class="pi pi-question-circle text-slate-400 cursor-help text-xs"
                [pTooltip]="help.saleType" tooltipPosition="left"
                tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
            </h2>
          </div>
          <ul class="divide-y divide-slate-100">
            @for (row of data()?.bySaleType ?? []; track row.saleType) {
              <li class="px-4 py-3 flex items-center justify-between gap-3">
                <div>
                  <p class="text-sm font-medium text-slate-800">{{ typeLabel(row.saleType) }}</p>
                  <p class="text-xs text-slate-400">{{ row.count }} ventas · ponderado {{ row.weightedAmount | currency:'MXN':'symbol-narrow':'1.0-0' }}</p>
                </div>
                <p class="text-sm font-semibold tabular-nums text-slate-900">
                  {{ row.amount | currency:'MXN':'symbol-narrow':'1.0-0' }}
                </p>
              </li>
            } @empty {
              <li class="px-4 py-8 text-center text-sm text-slate-400">Sin ventas abiertas</li>
            }
          </ul>
        </div>
      </div>
    </div>

    <!-- Stage cards -->
    <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
      @for (stage of data()?.stages ?? []; track stage.status) {
        <div class="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
          <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-500 truncate">{{ stage.label }}</p>
          <p class="mt-1 text-lg font-bold tabular-nums text-slate-900">
            {{ stage.amount | currency:'MXN':'symbol-narrow':'1.0-0' }}
          </p>
          <p class="text-xs text-slate-500">
            {{ stage.count }} · {{ stage.probability | percent:'1.0-0' }}
          </p>
          <p class="text-[11px] text-indigo-600 tabular-nums mt-0.5">
            Pond. {{ stage.weightedAmount | currency:'MXN':'symbol-narrow':'1.0-0' }}
          </p>
        </div>
      }
    </div>

    <!-- Deals table -->
    <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div class="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 class="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            Oportunidades
            <i class="pi pi-question-circle text-slate-400 cursor-help text-xs"
              [pTooltip]="tableTab() === 'open' ? help.openTable : help.lostTable"
              tooltipPosition="right"
              tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
          </h2>
          <p class="text-xs text-slate-500">Detalle del embudo comercial</p>
        </div>
        <div class="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
          <button type="button" (click)="setTableTab('open')"
            class="px-3 py-1.5"
            [class.bg-indigo-600]="tableTab() === 'open'"
            [class.text-white]="tableTab() === 'open'"
            [class.text-slate-600]="tableTab() !== 'open'">
            Abiertas ({{ data()?.openDeals?.length ?? 0 }})
          </button>
          <button type="button" (click)="setTableTab('lost')"
            class="px-3 py-1.5 border-l border-slate-200"
            [class.bg-rose-600]="tableTab() === 'lost'"
            [class.text-white]="tableTab() === 'lost'"
            [class.text-slate-600]="tableTab() !== 'lost'">
            Perdidas ({{ data()?.lostDeals?.length ?? 0 }})
          </button>
        </div>
      </div>

      <p-table
        [value]="tableRows()"
        [paginator]="true"
        [rows]="15"
        [rowsPerPageOptions]="[15, 30, 50]"
        styleClass="p-datatable-sm"
        [sortField]="tableTab() === 'open' ? 'weightedTotal' : 'total'"
        [sortOrder]="-1">
        <ng-template pTemplate="header">
          <tr>
            <th class="text-left">Venta</th>
            <th class="text-left hidden md:table-cell">Cliente</th>
            <th class="text-left">Etapa</th>
            <th class="text-left hidden sm:table-cell">Tipo</th>
            <th class="text-center hidden lg:table-cell">Días</th>
            <th class="text-right" pSortableColumn="total">Monto <p-sortIcon field="total" /></th>
            <th class="text-right hidden sm:table-cell">Prob.</th>
            <th class="text-right" pSortableColumn="weightedTotal">Ponderado <p-sortIcon field="weightedTotal" /></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td class="text-left">
              <a [routerLink]="['/sales', row.id]" class="text-sm text-slate-800 hover:text-indigo-700 inline-flex items-center gap-1.5">
                <i class="pi pi-shopping-bag text-indigo-500 text-xs"></i>
                {{ row.number || ('#' + row.id) }}
              </a>
              <p class="text-xs text-slate-400 ml-5">{{ formatDay(row.createdAt) }}</p>
            </td>
            <td class="text-left hidden md:table-cell text-sm text-slate-600">{{ row.customerName }}</td>
            <td class="text-left">
              <p-tag [value]="stageLabel(row.processStatus)" [severity]="stageSeverity(row.processStatus)" />
            </td>
            <td class="text-left hidden sm:table-cell text-sm text-slate-600">{{ typeLabel(row.saleType) }}</td>
            <td class="text-center hidden lg:table-cell tabular-nums text-sm text-slate-500">{{ row.daysInStage }}</td>
            <td class="text-right tabular-nums font-medium">
              {{ row.total | currency:'MXN':'symbol-narrow':'1.0-0' }}
            </td>
            <td class="text-right hidden sm:table-cell tabular-nums text-slate-500">
              {{ row.probability | percent:'1.0-0' }}
            </td>
            <td class="text-right tabular-nums text-indigo-700 font-semibold">
              {{ row.weightedTotal | currency:'MXN':'symbol-narrow':'1.0-0' }}
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="8" class="text-center text-slate-400 py-10 text-sm">
              No hay oportunidades en esta vista
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>

    <!-- Glossary -->
    <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <button type="button" (click)="toggleGlossary()"
        class="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-50">
        <span class="text-sm font-semibold text-slate-900 inline-flex items-center gap-2">
          <i class="pi pi-book text-indigo-500"></i>
          Glosario
        </span>
        <i class="pi text-slate-400" [class.pi-chevron-down]="!glossaryOpen()" [class.pi-chevron-up]="glossaryOpen()"></i>
      </button>
      @if (glossaryOpen()) {
        <ul class="border-t border-slate-100 divide-y divide-slate-100">
          @for (item of glossary; track item.term) {
            <li class="px-4 py-3">
              <p class="text-sm font-semibold text-slate-800">{{ item.term }}</p>
              <p class="text-xs text-slate-500 mt-0.5">{{ item.definition }}</p>
            </li>
          }
        </ul>
      }
    </div>

  </div>
</div>
